import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from '@appsumo/dorado-react';
import Image from 'next/image';
import { useWindowSize } from 'usehooks-ts';

type FounderLink = {
  title: string;
  url: string;
};

type Founder = {
  avatar: string;
  email_verified: boolean;
  first_name: string;
  is_staff: boolean;
  last_name: string;
  title: string;
  username: string;
};

type FounderPost = {
  message: string;
  title: string;
  user: Founder;
};

function FoundersProfile({
  user,
  productUrl,
}: {
  productUrl: string;
  user: Founder;
}) {
  const founderName = useMemo(
    () => [user.first_name, user.last_name].join(' ') || user.username,
    [user],
  );
  const url = useMemo(() => {
    if (!productUrl) return null;

    return {
      label: productUrl
        .replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
        .split('/')[0],
      value: productUrl,
    };
  }, [productUrl]);

  return (
    <div className="flex gap-x-6 grid-in-founder">
      <div>
        <Image
          src={user.avatar}
          width={100}
          height={100}
          alt={user.username}
          className="h-16 w-16 rounded-full md:h-24 md:w-24"
          unoptimized
        />
      </div>
      <div className="flex flex-col">
        <h4 className="font-header font-semibold md:text-2xl">{founderName}</h4>
        <div className="text-sm md:text-base">{user.title}</div>
        {url && (
          <div className="text-sm md:text-base">
            <Link href={url.value} styled external target="_blank">
              {url.label}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function FoundersLinks({ links }: { links: FounderLink[] }) {
  return (
    <div className="border-t border-t-gravel py-4">
      <h4 className="mb-4 font-header text-xl font-semibold">Helpful links</h4>
      <ul>
        {links.map((link) => (
          <li key={link.url}>
            <Link href={link.url} styled external target="_blank">
              {link.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FoundersPosts({
  posts,
  onClick,
}: {
  posts: FounderPost[];
  onClick: (index: number) => void;
}) {
  return (
    <div className="border-t border-t-gravel py-4">
      <h4 className="mb-4 font-header text-xl font-semibold">All posts</h4>
      <ul>
        {posts.map((post, index) => (
          <li key={post.title}>
            <Link styled href="#" onClick={() => onClick(index)}>
              {post.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface RichHtmlContentProps {
  htmlContent: string;
  mobileWidth: number;
  maxMobileHeight: number;
  maxDesktopHeight: number;
  className?: string;
}

const RichHtmlContent = ({
  htmlContent,
  mobileWidth,
  maxMobileHeight,
  maxDesktopHeight,
  className = '',
}: RichHtmlContentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(`${maxDesktopHeight}px`);
  const contentRef = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowSize();

  useEffect(() => {
    const isMobile = width < mobileWidth;
    const calculateMaxHeight = () => {
      const maxHeight = isMobile ? maxMobileHeight : maxDesktopHeight;
      if (contentRef.current) {
        const el = contentRef.current;
        const children = el.querySelectorAll(':scope > *');
        let currentHeight = 0;

        const linesToShow = Array.from(children).reduce((acc, child) => {
          if (currentHeight < maxHeight) {
            const style = getComputedStyle(child);
            const marginTop = parseFloat(style.marginTop);
            const elementHeight =
              (child as HTMLElement).offsetHeight + marginTop;
            currentHeight += elementHeight;

            if (currentHeight < maxHeight) {
              return acc + elementHeight;
            }
          }
          return acc;
        }, 0);

        if (linesToShow < maxHeight && currentHeight < maxHeight) {
          setIsExpanded(true);
          setMaxHeight('none');
        } else {
          setMaxHeight(`${linesToShow}px`);
        }
      }
    };

    calculateMaxHeight();
  }, [width, height, maxMobileHeight, maxDesktopHeight, mobileWidth]);

  const contentStyle = {
    maxHeight: isExpanded ? 'none' : maxHeight,
    overflow: 'hidden',
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      <div
        ref={contentRef}
        style={contentStyle}
        className={className}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      ></div>
      {maxHeight !== 'none' && (
        <button onClick={toggleExpanded}>
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </div>
  );
};

function FoundersPost({ post }: { post: FounderPost }) {
  return (
    <div className="flex flex-col gap-y-6 grid-in-body">
      <h6 className="text-sm text-slate md:text-base">{post.title}</h6>
      <RichHtmlContent
        className="prose-sm mb-4 md:prose"
        mobileWidth={768}
        maxMobileHeight={200}
        maxDesktopHeight={400}
        htmlContent={post.message}
      />
    </div>
  );
}

interface FoundersProps {
  productUrl: string;
  founders: {
    links: FounderLink[];
    posts: FounderPost[];
  };
}

export function Founders({ founders, productUrl }: FoundersProps) {
  const [postIndex, setPostIndex] = useState<number>(0);
  const currentPost = useMemo(
    () => founders.posts[postIndex],
    [postIndex, founders.posts],
  );

  if (!founders?.posts?.length) {
    return null;
  }

  return (
    <section
      id="from-the-founders"
      className="appsumo-breakout relative bg-iceberg py-8"
    >
      <div className="mx-auto max-w-7xl px-8">
        <h3 className="mb-6 font-header text-3xl font-bold">
          From the founders
        </h3>
        <div className="grid grid-cols-founders grid-rows-founders gap-y-4 gap-x-8 grid-areas-founders md:grid-cols-desktop-founders md:grid-rows-desktop-founders md:grid-areas-desktop-founders">
          <FoundersProfile user={currentPost.user} productUrl={productUrl} />
          <div className="flex flex-col gap-y-4 grid-in-links">
            <FoundersPosts
              posts={founders.posts}
              onClick={(index) => setPostIndex(index)}
            />
            <FoundersLinks links={founders.links} />
          </div>
          <FoundersPost post={currentPost} />
        </div>
      </div>
    </section>
  );
}
